<template>
  <!-- LotTraceDetail — 6 영역 통합 상세 컴포넌트 (Drawer/FullPage 양쪽 재사용) -->
  <!-- SPEC-CHINA-LOT-FLOW-DASHBOARD-001 REQ-FE-DETAIL-001, 004~009 -->
  <div class="lot-trace-detail">
    <!-- loading skeleton -->
    <template v-if="loading">
      <a-skeleton active :paragraph="{ rows: 3 }" style="margin-bottom: 16px;" />
      <a-skeleton active :paragraph="{ rows: 4 }" style="margin-bottom: 16px;" />
      <a-skeleton active :paragraph="{ rows: 4 }" />
    </template>

    <!-- 에러 -->
    <a-alert
      v-else-if="fetchError"
      type="error"
      :message="fetchError"
      show-icon
      style="margin-bottom: 16px;"
    >
      <template #action>
        <a-button size="small" @click="$emit('retry')">{{ $t('lotTrace_actions_retry') }}</a-button>
      </template>
    </a-alert>

    <template v-else-if="response">
      <!-- 영역 1: Summary Header Card — REQ-FE-DETAIL-004 -->
      <summary-header-card
        v-if="response.summary"
        :summary="response.summary"
      />

      <!-- REQ-FE-DETAIL-012: "전체 페이지로 보기" 링크 -->
      <div v-if="showFullPageLink && traceValue" style="margin-bottom: 16px; text-align: right;">
        <a-button type="link" size="small" @click="$emit('navigate', '/lot-trace/' + encodeURIComponent(traceValue))">
          {{ $t('lotFlow_detail_viewFullPage') }}
        </a-button>
      </div>

      <!-- Anomaly 배너 (기존 호환) -->
      <anomaly-banner :anomalies="response.anomalies || []" />

      <!-- 4 패널 vertical stack (REQ-FE-TRC-003) -->
      <div class="lot-trace-panels">
        <!-- Panel 1: ecount 본사 출하 (확장 필드 포함, REQ-FE-DETAIL-006) -->
        <ecount-panel
          :panel-data="response.ecountTransactions || emptyPanel"
          :loading="false"
          :value="traceValue"
          :anomalies="response.anomalies || []"
          @navigate="$emit('navigate', $event)"
        />

        <!-- Panel 2: 딜러 매입 (확장 필드 포함, REQ-FE-DETAIL-006) -->
        <dealer-purchase-panel
          :panel-data="response.dealerPurchaseInbounds || emptyPanel"
          :loading="false"
          :value="traceValue"
          :anomalies="response.anomalies || []"
          @navigate="$emit('navigate', $event)"
        />

        <!-- Panel 3: 딜러 재고 (확장 필드 + line chart, REQ-FE-DETAIL-005, 007) -->
        <dealer-inventory-panel
          :panel-data="response.dealerInventorySnapshots || emptyInventoryPanel"
          :loading="false"
          :value="traceValue"
          :anomalies="response.anomalies || []"
          :daily-trend="(response.dealerInventorySnapshots || {}).dailyTrend || []"
          :days-until-expiry="(response.dealerInventorySnapshots || {}).daysUntilExpiry"
          :product-description="(response.summary || {}).productDescription"
          @navigate="$emit('navigate', $event)"
        />

        <!-- Panel 4: 딜러 출고 (누적 출고율, REQ-FE-DETAIL-008) -->
        <dealer-sales-panel
          :panel-data="response.dealerSalesOutbounds || emptyPanel"
          :loading="false"
          :value="traceValue"
          :anomalies="response.anomalies || []"
          :cumulative-sales-rate="(response.dealerSalesOutbounds || {}).cumulativeSalesRate"
          @navigate="$emit('navigate', $event)"
        />
      </div>

      <!-- 영역 6: Anomaly 타임라인 — REQ-FE-DETAIL-009 -->
      <anomaly-timeline-panel
        :anomaly-timeline="response.anomalyTimeline || []"
      />
    </template>
  </div>
</template>

<script>
// @MX:ANCHOR: [AUTO] LotTraceDetail — Drawer/FullPage 양쪽에서 재사용하는 6 영역 통합 컴포넌트
// @MX:REASON: [AUTO] LotTrace.vue(FullPage) + LotTraceDrawer.vue(Drawer) 양쪽 fan_in >= 2. 확장 응답 schema 구독 단일 진입점.
import AnomalyBanner from './components/AnomalyBanner.vue';
import EcountPanel from './components/EcountPanel.vue';
import DealerPurchasePanel from './components/DealerPurchasePanel.vue';
import DealerInventoryPanel from './components/DealerInventoryPanel.vue';
import DealerSalesPanel from './components/DealerSalesPanel.vue';
import SummaryHeaderCard from './components/SummaryHeaderCard.vue';
import AnomalyTimelinePanel from './components/AnomalyTimelinePanel.vue';

export default {
  name: 'LotTraceDetail',
  components: {
    AnomalyBanner,
    EcountPanel,
    DealerPurchasePanel,
    DealerInventoryPanel,
    DealerSalesPanel,
    SummaryHeaderCard,
    AnomalyTimelinePanel,
  },
  props: {
    // GET /lot-trace/:value 응답 전체 (additive 확장 포함)
    response: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    fetchError: {
      type: String,
      default: null,
    },
    // 추적 중인 lot value (라우트 param 또는 Drawer에서 전달)
    traceValue: {
      type: String,
      default: '',
    },
    // Drawer 모드에서는 "전체 페이지로 보기" 링크 표시 (REQ-FE-DETAIL-012)
    showFullPageLink: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['navigate', 'retry'],
  computed: {
    emptyPanel() {
      return { count: 0, preCapTotal: 0, truncated: false, rows: [] };
    },
    emptyInventoryPanel() {
      return {
        count: 0, preCapTotal: 0, truncated: false, rows: [],
        currentQuantity: 0, latestSnapshotDate: null,
        dailyTrend: [], daysUntilExpiry: null,
      };
    },
  },
};
</script>

<style scoped>
.lot-trace-detail {
  width: 100%;
}
.lot-trace-panels {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
@media (max-width: 480px) {
  .lot-trace-panels {
    gap: 12px;
  }
}
</style>
